import { useState, useCallback, useEffect } from "react";
import { Plus, Search, Download, UserPlus } from "lucide-react";
import {
  useLeads,
  useLeadDetail,
  useLeadStats,
  useExportLeads,
  useCreateLead,
  useUpdateLead,
  useUpdateLeadStatus,
  useAddLeadInteraction,
  useAssignLead,
  useDeleteLead,
  useConvertLead,
} from "../hooks";
import { LeadStatsBar } from "./LeadStatsBar";
import { LeadsTable } from "./LeadsTable";
import { LeadDetailDrawer } from "./LeadDetailDrawer";
import { CreateLeadDialog } from "./CreateLeadDialog";
import { EditLeadDialog } from "./EditLeadDialog";
import type { EditLeadFormData } from "./EditLeadDialog";
import { UpdateStatusDialog } from "./UpdateStatusDialog";
import { AssignLeadDialog } from "./AssignLeadDialog";
import { ConvertLeadDialog } from "./ConvertLeadDialog";
import type { Lead, LeadStatus, LeadTier, LeadSource, InteractionType, ConvertLeadInput } from "../types";
import type { CreateLeadFormData } from "./CreateLeadDialog";
import { LEAD_STATUSES, LEAD_TIERS, LEAD_SOURCES, extractErrorMessage } from "../constants";
import { useLocale } from "../../../context/LanguageContext";

const PAGE_SIZE = 20;

export function LeadsPage() {
  const { t } = useLocale();

  // ─── State ───────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "ALL">("ALL");
  const [tierFilter, setTierFilter] = useState<LeadTier | "ALL">("ALL");
  const [sourceFilter, setSourceFilter] = useState<LeadSource | "ALL">("ALL");
  const [page, setPage] = useState(0);

  // Dialogs / drawers
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [statusDialogLead, setStatusDialogLead] = useState<Lead | null>(null);
  const [assignDialogLead, setAssignDialogLead] = useState<Lead | null>(null);
  const [convertDialogLead, setConvertDialogLead] = useState<Lead | null>(null);
  const [editDialogLead, setEditDialogLead] = useState<Lead | null>(null);

  // Error states for user-facing feedback
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [convertError, setConvertError] = useState<string | null>(null);

  // ─── Queries ─────────────────────────────────────────────────────────
  const { data, loading } = useLeads({
    search: debouncedSearch || undefined,
    status: statusFilter !== "ALL" ? [statusFilter] : undefined,
    tier: tierFilter !== "ALL" ? [tierFilter] : undefined,
    source: sourceFilter !== "ALL" ? [sourceFilter] : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const leads = data?.adminLeads?.items ?? [];
  const total = data?.adminLeads?.total ?? 0;

  const { data: statsData, loading: statsLoading } = useLeadStats();
  const stats = statsData?.adminLeadStats ?? undefined;

  const { data: detailData, loading: detailLoading } = useLeadDetail(selectedLeadId ?? "");
  const selectedLead: Lead | null = detailData?.adminLeadDetail ?? null;

  const [exportLeads] = useExportLeads();

  // ─── Mutations ───────────────────────────────────────────────────────
  const [createLead, { loading: creating }] = useCreateLead();
  const [updateLead, { loading: updating }] = useUpdateLead();
  const [updateLeadStatus, { loading: updatingStatus }] = useUpdateLeadStatus();
  const [addLeadInteraction, { loading: addingInteraction }] = useAddLeadInteraction();
  const [assignLead, { loading: assigning }] = useAssignLead();
  const [deleteLead, { loading: deleting }] = useDeleteLead();
  const [convertLead, { loading: converting }] = useConvertLead();

  // ─── Debounced search ────────────────────────────────────────────────
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleCreate = useCallback(
    async (formData: CreateLeadFormData) => {
      setCreateError(null);
      try {
        await createLead({ variables: { input: formData } });
        setShowCreateDialog(false);
      } catch (err) {
        const msg = extractErrorMessage(err) ?? t("leads.errors.createFailed");
        setCreateError(msg);
      }
    },
    [createLead, t],
  );

  const handleUpdateStatus = useCallback(
    async (leadId: string, status: LeadStatus, note?: string) => {
      try {
        await updateLeadStatus({ variables: { input: { leadId, status, note } } });
        setStatusDialogLead(null);
      } catch (err) {
        console.error("[LeadsPage] updateLeadStatus error:", err);
      }
    },
    [updateLeadStatus],
  );

  const handleEdit = useCallback(
    async (leadId: string, data: EditLeadFormData) => {
      setEditError(null);
      try {
        await updateLead({ variables: { id: leadId, input: data } });
        setEditDialogLead(null);
      } catch (err) {
        const msg = extractErrorMessage(err) ?? t("leads.errors.updateFailed");
        setEditError(msg);
      }
    },
    [updateLead, t],
  );

  const handleAddInteraction = useCallback(
    async (leadId: string, type: InteractionType, content: string) => {
      try {
        await addLeadInteraction({ variables: { input: { leadId, type, content } } });
      } catch (err) {
        console.error("[LeadsPage] addLeadInteraction error:", err);
      }
    },
    [addLeadInteraction],
  );

  const handleAssign = useCallback(
    async (adminUserId: string) => {
      if (!assignDialogLead) return;
      try {
        await assignLead({
          variables: { input: { leadId: assignDialogLead._id, assigneeId: adminUserId } },
        });
        setAssignDialogLead(null);
      } catch (err) {
        console.error("[LeadsPage] assignLead error:", err);
      }
    },
    [assignLead, assignDialogLead],
  );

  const handleDelete = useCallback(
    async (leadId: string) => {
      try {
        await deleteLead({ variables: { id: leadId } });
        setSelectedLeadId(null);
      } catch (err) {
        console.error("[LeadsPage] deleteLead error:", err);
      }
    },
    [deleteLead],
  );

  const handleConvert = useCallback(
    async (input: Omit<ConvertLeadInput, "leadId"> & { leadId: string }) => {
      setConvertError(null);
      try {
        await convertLead({ variables: { input } });
        setConvertDialogLead(null);
        setSelectedLeadId(null);
      } catch (err) {
        const msg = extractErrorMessage(err) ?? t("leads.errors.convertFailed");
        setConvertError(msg);
      }
    },
    [convertLead, t],
  );

  const handleExport = useCallback(async () => {
    try {
      const result = await exportLeads({
        variables: {
          filters: {
            status: statusFilter !== "ALL" ? [statusFilter] : undefined,
            tier: tierFilter !== "ALL" ? [tierFilter] : undefined,
            source: sourceFilter !== "ALL" ? sourceFilter : undefined,
          },
        },
      });
      const csv = result.data?.adminExportLeads;
      if (csv) {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("[LeadsPage] exportLeads error:", err);
    }
  }, [exportLeads, statusFilter, tierFilter, sourceFilter]);

  const handleRowClick = useCallback((lead: Lead) => {
    setSelectedLeadId(lead._id);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <UserPlus className="h-7 w-7" />
            {t("leads.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("leads.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-border px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Download className="h-4 w-4" />
            {t("common.exportCsv")}
          </button>
          <button
            type="button"
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            {t("leads.createLead")}
          </button>
        </div>
      </div>

      {/* Stats */}
      <LeadStatsBar stats={stats} loading={statsLoading} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("leads.search")}
            className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as LeadStatus | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{t("leads.filter.allStatuses")}</option>
          {LEAD_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`leads.status.${s}`)}
            </option>
          ))}
        </select>

        {/* Tier filter */}
        <select
          value={tierFilter}
          onChange={(e) => {
            setTierFilter(e.target.value as LeadTier | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{t("leads.filter.allTiers")}</option>
          {LEAD_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {t(`leads.tier.${tier}`)}
            </option>
          ))}
        </select>

        {/* Source filter */}
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value as LeadSource | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{t("leads.filter.allSources")}</option>
          {LEAD_SOURCES.map((s) => (
            <option key={s} value={s}>
              {t(`leads.source.${s}`)}
            </option>
          ))}
        </select>

        {/* Count badge */}
        <span className="ml-auto text-sm text-muted-foreground">
          {total} {t("leads.leadCount")}
        </span>
      </div>

      {/* Table */}
      <LeadsTable
        leads={leads}
        total={total}
        isLoading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onSelect={handleRowClick}
      />

      {/* Detail Drawer */}
      {selectedLead && (
        <LeadDetailDrawer
          lead={selectedLead}
          onClose={() => setSelectedLeadId(null)}
          onUpdateStatus={handleUpdateStatus}
          onAddInteraction={handleAddInteraction}
          onDelete={handleDelete}
          onConvert={(lead) => { setConvertError(null); setConvertDialogLead(lead); }}
          onEdit={(lead) => { setEditError(null); setEditDialogLead(lead); }}
          loading={detailLoading || updatingStatus || addingInteraction || deleting || converting || updating}
        />
      )}

      {/* Dialogs */}
      <CreateLeadDialog
        open={showCreateDialog}
        onClose={() => { setShowCreateDialog(false); setCreateError(null); }}
        onSubmit={handleCreate}
        loading={creating}
        error={createError}
      />

      {statusDialogLead && (
        <UpdateStatusDialog
          open={true}
          currentStatus={statusDialogLead.status}
          leadName={statusDialogLead.name}
          onClose={() => setStatusDialogLead(null)}
          onConfirm={(status, note) =>
            handleUpdateStatus(statusDialogLead._id, status, note)
          }
          loading={updatingStatus}
        />
      )}

      {assignDialogLead && (
        <AssignLeadDialog
          open={true}
          leadName={assignDialogLead.name}
          currentAssigneeId={assignDialogLead.assignedTo}
          onClose={() => setAssignDialogLead(null)}
          onConfirm={handleAssign}
          loading={assigning}
        />
      )}

      {convertDialogLead && (
        <ConvertLeadDialog
          open={true}
          lead={convertDialogLead}
          onClose={() => { setConvertDialogLead(null); setConvertError(null); }}
          onConfirm={handleConvert}
          loading={converting}
          error={convertError}
        />
      )}

      {editDialogLead && (
        <EditLeadDialog
          open={true}
          lead={editDialogLead}
          onClose={() => { setEditDialogLead(null); setEditError(null); }}
          onSubmit={handleEdit}
          loading={updating}
          error={editError}
        />
      )}
    </div>
  );
}
